import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Search as SearchIcon,
  QrCode2 as QrCodeIcon,
  WorkspacePremium as CertificateIcon,
} from '@mui/icons-material';
import axios from 'axios';

const CERTIFICATE_ISSUER = 'KNLS Computers';

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (typeof window !== 'undefined' && (window.location.hostname.includes('ondigitalocean.app') || window.location.hostname.includes('services.knls.in'))) {
    return '/api';
  }
  return 'http://localhost:3000/api';
};

const API_BASE_URL = getApiBaseUrl();

const decodeCertificatePayload = (encoded) => {
  if (!encoded) return null;

  try {
    const base64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const payload = JSON.parse(decodeURIComponent(escape(atob(padded))));
    return {
      certificateId: payload.certificateId || payload.cid,
      institute: payload.institute || payload.inst,
      name: payload.name || payload.n,
      fatherName: payload.fatherName || payload.f,
      courseName: payload.courseName || payload.c,
      durationFrom: payload.durationFrom || payload.df,
      durationTo: payload.durationTo || payload.dt,
      grade: payload.grade || payload.g,
      issueDate: payload.issueDate || payload.iss,
      studentId: payload.studentId || payload.sid,
      phone: payload.phone || payload.p,
      status: payload.status || payload.st,
      dashboardUrl: payload.dashboardUrl || payload.u,
    };
  } catch (error) {
    console.error('Failed to decode certificate:', error);
    return null;
  }
};

const parseMonthValue = (value) => {
  if (!value) return null;
  const rawValue = String(value);
  const dateValue = /^\d{4}-\d{2}$/.test(rawValue) ? `${rawValue}-01` : rawValue;
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return null;
  return {
    date,
    index: date.getFullYear() * 12 + date.getMonth(),
  };
};

const formatMonthYear = (value) => {
  const parsed = parseMonthValue(value);
  if (!parsed) return value || 'N/A';
  return parsed.date.toLocaleString('en-IN', { month: 'short', year: 'numeric' });
};

const getDurationMonths = (durationFrom, durationTo) => {
  const from = parseMonthValue(durationFrom);
  const to = parseMonthValue(durationTo);
  if (!from || !to) return 'N/A';
  const elapsedMonths = to.index - from.index;
  const months = Math.max(1, elapsedMonths);
  return `${months} ${months === 1 ? 'Month' : 'Months'}`;
};

const CertificateVerification = () => {
  const [searchParams] = useSearchParams();
  const legacyCertificate = decodeCertificatePayload(searchParams.get('c'));
  const [certificateId, setCertificateId] = useState(
    searchParams.get('id') || legacyCertificate?.certificateId || '',
  );
  const [certificate, setCertificate] = useState(null);
  const [status, setStatus] = useState({ loading: false, error: '' });

  const verifyCertificate = async (id = certificateId) => {
    const trimmedId = String(id || '').trim().toUpperCase();
    if (!trimmedId) {
      setStatus({ loading: false, error: 'Please enter a certificate ID.' });
      setCertificate(null);
      return;
    }

    setStatus({ loading: true, error: '' });
    setCertificate(null);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/students/certificates/verify/${encodeURIComponent(trimmedId)}`,
      );
      setCertificate(response.data?.certificate || null);
      setStatus({ loading: false, error: '' });
    } catch (error) {
      setCertificate(null);
      setStatus({
        loading: false,
        error: error.response?.data?.message || 'Certificate could not be verified. Please check the ID and try again.',
      });
    }
  };

  useEffect(() => {
    const initialId = searchParams.get('id') || legacyCertificate?.certificateId;
    if (initialId) {
      verifyCertificate(initialId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        py: 5,
        background:
          'radial-gradient(circle at top left, rgba(25,118,210,0.18), transparent 28%), linear-gradient(135deg, #f7fbff 0%, #fffaf0 100%)',
      }}
    >
      <Container maxWidth="md">
        <Card sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: 8 }}>
          <Box sx={{ bgcolor: '#15346f', color: '#fff', p: 3, textAlign: 'center' }}>
            <CertificateIcon sx={{ fontSize: 56, mb: 1, color: '#d7b66d' }} />
            <Typography variant="h4" sx={{ fontWeight: 900 }}>
              Certificate Verification
            </Typography>
            <Typography sx={{ opacity: 0.9 }}>
              Official credential validation portal for {CERTIFICATE_ISSUER}
            </Typography>
          </Box>
          <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
            <Box
              component="form"
              onSubmit={(event) => {
                event.preventDefault();
                verifyCertificate();
              }}
              sx={{
                display: 'flex',
                gap: 1.5,
                flexDirection: { xs: 'column', sm: 'row' },
                mb: 3,
              }}
            >
              <TextField
                label="Certificate ID"
                placeholder="Example: KNLS-CERT-26000078"
                value={certificateId}
                onChange={(event) => setCertificateId(event.target.value.toUpperCase())}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <QrCodeIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                variant="contained"
                startIcon={<SearchIcon />}
                disabled={status.loading}
                sx={{ minWidth: 160 }}
              >
                {status.loading ? 'Verifying...' : 'Verify'}
              </Button>
            </Box>

            {status.error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {status.error}
              </Alert>
            )}

            {!certificate && !status.error && (
              <Alert severity="info" sx={{ mb: 3 }}>
                Enter the certificate ID printed on the certificate. KNLS will validate the ID against official student records.
              </Alert>
            )}

            {certificate && (() => {
              const details = [
                ['Certificate ID', certificate.certificateId],
                ['Student Name', certificate.studentName],
                ['Father Name', certificate.fatherName],
                ['Course Name', certificate.courseName],
                ['Duration From', formatMonthYear(certificate.durationFrom)],
                ['Duration To', formatMonthYear(certificate.durationTo)],
                ['Total Duration', getDurationMonths(certificate.durationFrom, certificate.durationTo)],
                ['Student ID', certificate.studentId],
              ];

              return (
                <>
                <Alert
                  severity="success"
                  icon={<CheckCircleIcon />}
                  sx={{ mb: 3, alignItems: 'center' }}
                >
                  Certificate verified successfully. This credential is valid in KNLS records.
                </Alert>

                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Chip
                    color="success"
                    icon={<CheckCircleIcon />}
                    label={certificate.status || 'Valid'}
                    sx={{ fontWeight: 800, mb: 1 }}
                  />
                  <Typography variant="h5" sx={{ fontWeight: 900, color: '#7a1111' }}>
                    {certificate.studentName || 'Student Name'}
                  </Typography>
                  <Typography color="text.secondary">
                    Completed <strong>{certificate.courseName || 'Course'}</strong>
                  </Typography>
                </Box>

                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={2}>
                  {details.map(([label, value]) => (
                    <Grid item xs={12} sm={6} key={label}>
                      <Box
                        sx={{
                          border: '1px solid #e3e8ef',
                          borderRadius: 2,
                          p: 1.5,
                          bgcolor: '#fff',
                          height: '100%',
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
                          {label}
                        </Typography>
                        <Typography sx={{ fontWeight: 800, wordBreak: 'break-word' }}>
                          {value || 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center', mt: 4 }}>
                  <Button
                    variant="contained"
                    startIcon={<QrCodeIcon />}
                    onClick={() => window.print()}
                  >
                    Print Verification
                  </Button>
                </Box>
                </>
              );
            })()}

            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Typography variant="body2" color="text.secondary">
                For manual confirmation, contact KNLS Computers with the certificate ID shown on the certificate.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default CertificateVerification;
